// Pure prospect-board logic, ported from src/pwhlstats/web/prospects.py.
// No DOM / no DB imports, so the Node parity harness (js/test-cli.ts) can run
// it directly and tests/test_prospects_port.py can assert it matches Python.
// draft.ts adds the DB query + DOM rendering on top.

export const SKATER_COLUMNS: [string, string][] = [
  ["GP", "Games Played"], ["G", "G"], ["A", "A"], ["PTS", "PTS"],
  ["SOG", "Shots"], ["BLK", "Blocks"], ["PIM", "PIM"], ["PM", "+/-"],
];
export const GOALIE_COLUMNS: [string, string][] = [
  ["GP", "Games Played"], ["W", "W"], ["L", "L"], ["SVP", "SV%"],
  ["GAA", "GAA"], ["SO", "SO"],
];
export const STAT_HEADER_SLUGS: Record<string, string> = {
  "Games Played": "gp", G: "g", A: "a", PTS: "pts", Shots: "sog",
  Blocks: "blk", PIM: "pim", "+/-": "plus_minus", W: "w", L: "l",
  "SV%": "sv_pct", GAA: "gaa", SO: "so",
};
// (code, display, source_url) in page-filter order. Mirrors prospects.LEAGUES.
export const LEAGUES: [string, string, string][] = [
  ["NCAA", "NCAA Division I", "https://www.eliteprospects.com/league/ncaa-w"],
  ["NCAA DIII", "NCAA Division III", "https://www.eliteprospects.com/league/ncaa-iii-w"],
  ["USports", "U Sports (Canada)", "https://www.eliteprospects.com/league/usports-w"],
  ["SDHL", "SDHL (Sweden)", "https://www.eliteprospects.com/league/sdhl-w"],
  ["SWHL", "SWHL (Switzerland)", "https://www.eliteprospects.com/league/swhl-w"],
  ["EWHL", "EWHL (Europe)", "https://www.eliteprospects.com/league/ewhl-w"],
  ["DFEL", "DFEL (Germany)", "https://www.eliteprospects.com/league/dfel-w"],
  ["Italy", "Italy (W)", "https://www.eliteprospects.com/league/italy-w"],
  ["CCWHA", "CCWHA (US college club)", ""],
  ["ZhHL", "ZhHL (Russia)", "https://www.eliteprospects.com"],
  ["Spain", "Spain", "https://www.eliteprospects.com"],
  ["Japan", "Japan", "https://www.eliteprospects.com"],
  ["Taiwan", "Chinese Taipei", "https://www.eliteprospects.com"],
  ["Sweden", "Sweden (other)", "https://www.eliteprospects.com"],
  ["Équipe nationale de France", "France (national team)", ""],
  ["DNP", "Did not play 2025-26", ""],
];
export const LEAGUE_NAMES: Record<string, string> = Object.fromEntries(
  LEAGUES.map(([code, name]) => [code, name]),
);
export const POSITION_GROUPS: [string, string][] = [
  ["F", "Forwards"], ["D", "Defenders"], ["G", "Goaltenders"],
];

export interface Prospect {
  last_name: string;
  first_name: string;
  position: string;
  nationality: string;
  club: string;
  league: string;
  ep_url: string;
  stats: Record<string, unknown> | null;
  rank: number | null;
}

export function fullName(p: Prospect): string {
  return `${p.first_name} ${p.last_name}`.trim();
}
export function groupFor(position: string): "G" | "D" | "F" {
  const pos = (position || "").toUpperCase();
  if (pos === "G") return "G";
  if (pos === "D") return "D";
  return "F";
}
export function isGoalie(p: Prospect): boolean {
  return groupFor(p.position) === "G";
}
export function hasStats(p: Prospect): boolean {
  return !!p.stats && p.stats["GP"] !== null && p.stats["GP"] !== undefined;
}
export function isTopProspect(p: Prospect): boolean {
  return p.rank !== null && p.rank !== undefined;
}
export function leagueName(p: Prospect): string {
  return LEAGUE_NAMES[p.league] ?? p.league;
}
export function statCells(p: Prospect): [string, string][] {
  const cols = isGoalie(p) ? GOALIE_COLUMNS : SKATER_COLUMNS;
  return cols.map(([key, header]) => {
    const v = (p.stats ?? {})[key];
    return [header, v === null || v === undefined ? "—" : String(v)] as [string, string];
  });
}
export function sortValue(p: Prospect): number {
  if (!hasStats(p)) return -1;
  const key = isGoalie(p) ? "SVP" : "PTS";
  const raw = (p.stats ?? {})[key];
  // Match Python float(None|"" |non-numeric) → -1. Number() would coerce
  // null/"" to 0, which mis-ranks statless goalies above real ones.
  if (raw === null || raw === undefined || (typeof raw === "string" && raw.trim() === "")) {
    return -1;
  }
  const v = Number(raw);
  return Number.isFinite(v) ? v : -1;
}
export function byPosition(all: Prospect[], group: string): Prospect[] {
  // Tiebreak by codepoint (NOT localeCompare) to match Python's str ordering
  // in prospects.by_position — keeps the JS↔Python parity test exact.
  return all
    .filter((p) => groupFor(p.position) === group)
    .sort((a, b) =>
      sortValue(b) - sortValue(a) ||
      (a.last_name < b.last_name ? -1 : a.last_name > b.last_name ? 1 : 0));
}
export function leaguesPresent(all: Prospect[]): [string, string, string, number][] {
  const counts: Record<string, number> = {};
  for (const p of all) counts[p.league] = (counts[p.league] ?? 0) + 1;
  const out: [string, string, string, number][] = [];
  const seen = new Set<string>();
  for (const [code, name, url] of LEAGUES) {
    if (counts[code]) { out.push([code, name, url, counts[code]!]); seen.add(code); }
  }
  for (const [code, n] of Object.entries(counts)) {
    if (!seen.has(code)) out.push([code, code || "Other / unlisted", "", n]);
  }
  return out;
}
export function summarize(all: Prospect[]): Record<string, number> {
  const groups = { F: 0, D: 0, G: 0 };
  for (const p of all) groups[groupFor(p.position)] += 1;
  return {
    total: all.length,
    forwards: groups.F, defenders: groups.D, goalies: groups.G,
    with_stats: all.filter(hasStats).length,
    leagues: new Set(all.filter((p) => p.league !== "DNP").map((p) => p.league)).size,
    top_prospects: all.filter(isTopProspect).length,
  };
}
